function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

const FACETS_ADVANCED_OPEN_STORAGE_KEY = 'facetsAdvancedOpenState';

function getAdvancedFacetLabel(filter) {
  return filter.querySelector('.facets__summary-label, .body-title-m')?.textContent?.trim() || '';
}

function getAdvancedFacetStorageScope() {
  return window.location.pathname;
}

function readAdvancedFacetOpenState() {
  try {
    const rawState = localStorage.getItem(FACETS_ADVANCED_OPEN_STORAGE_KEY);
    return rawState ? JSON.parse(rawState) : {};
  } catch (_) {
    return {};
  }
}

function writeAdvancedFacetOpenState(state) {
  try {
    localStorage.setItem(FACETS_ADVANCED_OPEN_STORAGE_KEY, JSON.stringify(state));
  } catch (_) {}
}

function getStoredAdvancedFacetOpenLabels() {
  const state = readAdvancedFacetOpenState();
  const labels = state[getAdvancedFacetStorageScope()];
  return Array.isArray(labels) ? labels : [];
}

function persistAdvancedFacetOpenState(filter, open) {
  if (filter.dataset.advancedFilter !== 'true') return;

  const label = getAdvancedFacetLabel(filter);
  if (!label) return;

  const scope = getAdvancedFacetStorageScope();
  const state = readAdvancedFacetOpenState();
  const labels = new Set(Array.isArray(state[scope]) ? state[scope] : []);

  if (open) {
    labels.add(label);
  } else {
    labels.delete(label);
  }

  state[scope] = Array.from(labels);
  writeAdvancedFacetOpenState(state);
}

function applyStoredAdvancedFacetOpenState(form) {
  const storedLabels = new Set(getStoredAdvancedFacetOpenLabels());

  form.querySelectorAll('.js-filter[data-advanced-filter="true"]').forEach((filter) => {
    const label = getAdvancedFacetLabel(filter);
    if (!label || !storedLabels.has(label)) return;

    filter.setAttribute('open', '');
    const summary = filter.querySelector('.facets__summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  });
}

function clearFacetDisclosureOpening(filter) {
  filter.classList.remove('facets-disclosure--opening');
}

function markFacetDisclosureOpening(filter) {
  filter.classList.add('facets-disclosure--opening');
  const clear = () => clearFacetDisclosureOpening(filter);
  filter.addEventListener('animationend', clear, { once: true });
  window.setTimeout(clear, 500);
}

function suppressFacetEnterAnimations() {
  document.documentElement.classList.add('facets-updating');
}

function releaseFacetEnterAnimations() {
  document.querySelectorAll('.facets-disclosure--opening').forEach(clearFacetDisclosureOpening);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('facets-updating');
    });
  });
}

const FACET_FORM_MOBILE_MEDIA = '(max-width: 749px)';
const FACET_DROPDOWN_SCROLL_BUFFER_PX = 20;

function getFacetFiltersScrollContainer(filter, form) {
  const formVertical = form?.classList?.contains('facets__form-vertical')
    ? form
    : form?.closest?.('.facets__form-vertical');

  if (formVertical && formVertical.scrollHeight > formVertical.clientHeight + 1) {
    return formVertical;
  }

  let scrollContainer = null;
  let parent = filter.parentElement;

  while (parent && parent !== document.body) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    const canScroll = parent.scrollHeight > parent.clientHeight + 1;

    if (canScroll && (overflowY === 'auto' || overflowY === 'scroll')) {
      scrollContainer = parent;
      break;
    }

    parent = parent.parentElement;
  }

  return (
    scrollContainer ||
    [form, form.closest('facet-filters-form'), form.closest('.facets-wrapper'), form.closest('menu-drawer')].find(
      (element) => element && element.scrollHeight > element.clientHeight + 1,
    ) ||
    form
  );
}

function getActiveFacetsFooterRect(form) {
  if (!window.matchMedia(FACET_FORM_MOBILE_MEDIA).matches) return null;

  const menuDrawer = form.closest('menu-drawer');
  if (!menuDrawer?.classList.contains('is-open')) return null;

  const footer = menuDrawer.querySelector('.active-facets__footer');
  if (!footer) return null;

  const style = window.getComputedStyle(footer);
  if (style.display === 'none' || style.visibility === 'hidden') return null;

  const rect = footer.getBoundingClientRect();
  if (rect.height <= 0 || rect.width <= 0) return null;

  return rect;
}

function scrollFacetDropdownIntoView(filter, form) {
  const scrollContainer = getFacetFiltersScrollContainer(filter, form);
  const trigger = filter.querySelector('.custom-dropdown .dropdown-trigger');
  if (!trigger) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const performScroll = () => {
    if (trigger.getAttribute('aria-expanded') !== 'true') return;

    const dropdownContent = filter.querySelector('.custom-dropdown .dropdown-content');
    if (!dropdownContent) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const footerRect = getActiveFacetsFooterRect(form);
    const visibleBottom = footerRect ? footerRect.top : containerRect.bottom;
    const filterRect = filter.getBoundingClientRect();
    const scrollDelta = filterRect.bottom - visibleBottom + (footerRect ? FACET_DROPDOWN_SCROLL_BUFFER_PX : 0);

    if (scrollDelta <= 0) return;

    const maxTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    scrollContainer.scrollTo({
      top: Math.min(maxTop, scrollContainer.scrollTop + scrollDelta),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(performScroll);
  });
}

function relocateFacetFiltersForm() {
  const form = document.getElementById('FacetFiltersForm');
  const mobileMount = document.getElementById('facet-form-mount-mobile');
  const desktopMount = document.getElementById('facet-form-mount-desktop');
  if (!form || !mobileMount || !desktopMount) return;
  const preferMobile = window.matchMedia(FACET_FORM_MOBILE_MEDIA).matches;
  const target = preferMobile ? mobileMount : desktopMount;
  if (form.parentElement !== target) target.appendChild(form);
}

window.relocateFacetFiltersForm = relocateFacetFiltersForm;

function bindFacetFormRelocateListeners() {
  if (bindFacetFormRelocateListeners._bound) return;
  bindFacetFormRelocateListeners._bound = true;
  const mq = window.matchMedia(FACET_FORM_MOBILE_MEDIA);
  const handler = () => relocateFacetFiltersForm();
  if (mq.addEventListener) mq.addEventListener('change', handler);
  else mq.addListener(handler);
  window.addEventListener('resize', handler);
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.hasUnsubmittedChanges = false;

    this.pendingChanges = new Map();
    this.isRequestInProgress = false;
    this.abortController = null;

    this.onConfirmFilters = this.onConfirmFilters.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      const formData = new FormData(event.target.closest('form'));
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    }, 250);

    this.debouncedFilterSubmit = debounce((formElement, event) => {
      if (formElement.isRequestInProgress) {
        if (formElement.abortController) {
          formElement.abortController.abort();
        }
      }

      const form = formElement.querySelector('form');
      if (form) {
        const searchParams = formElement.createSearchParams(form);
        formElement.onSubmitForm(searchParams, event);
      }

      formElement.pendingChanges.clear();
    }, 300);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    this.setupCustomDropdowns();
    this.constructor.organizeAdvancedFilters(this);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    const formVertical = this.querySelector('.facets__form-vertical');
    if (formVertical && this.closest('.facets-vertical') && !formVertical.dataset.scrollbarBound) {
      formVertical.dataset.scrollbarBound = '1';
      let scrollTimeout;
      formVertical.addEventListener('scroll', () => {
        formVertical.classList.add('is-scrolling');
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => formVertical.classList.remove('is-scrolling'), 600);
      });
    }
  }

  onConfirmFilters(event) {
    event.preventDefault();

    if (!this.hasUnsubmittedChanges) return;

    this.hasUnsubmittedChanges = false;
    this.updateConfirmButton();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('reset_filter_')) {
        const parts = key.replace('reset_filter_', '').split(new RegExp('_([^_]*)$'));
        if (parts.length >= 2) {
          const name = parts[0];
          const value = parts[1];
          resetFilter(name, value);
          localStorage.removeItem(key);
        }
      }
    }

    synchronizeVisualToCheckboxState();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const combinedParams = new URLSearchParams();

    sortFilterForms.forEach((form) => {
      if (
        form.id === 'FacetSortForm' ||
        form.id === 'FacetFiltersForm' ||
        form.id === 'FacetFiltersFormMobile' ||
        form.id === 'FacetSortDrawerForm'
      ) {
        const formParams = new URLSearchParams(this.createSearchParams(form));

        for (const [key, value] of formParams.entries()) {
          if (!combinedParams.getAll(key).includes(value)) {
            combinedParams.append(key, value);
          }
        }
      }
    });

    const searchParams = combinedParams.toString();
    FacetFiltersForm.updateURLHash(searchParams);
    this.onSubmitForm(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const searchParams = this.createSearchParams(event.target.closest('form'));
    this.onSubmitForm(searchParams, event);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      let searchParams = event.state?.searchParams;
      if (searchParams == null) {
        searchParams = window.location.search.startsWith('?')
          ? window.location.search.slice(1)
          : FacetFiltersForm.searchParamsInitial;
      }
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    document.addEventListener('sort-update', (event) => {
      FacetFiltersForm.renderPage(event.detail.searchParams, null, true);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = false) {
    if (FacetFiltersForm.currentAbortController) {
      FacetFiltersForm.currentAbortController.abort();
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.add('loading');
      }
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    if (FacetFiltersForm.loadingTimeout) {
      clearTimeout(FacetFiltersForm.loadingTimeout);
    }
    FacetFiltersForm.loadingTimeout = setTimeout(() => {
      FacetFiltersForm.removeLoadingState();
    }, 10000);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    FacetFiltersForm.fetchSeoBrandTextSection(searchParams);
    FacetFiltersForm.fetchCollectionSeoCardSection(searchParams);
    FacetFiltersForm.fetchCollectionTrustSection(searchParams);
    FacetFiltersForm.fetchCollectionBrandModelsSection(searchParams);
    FacetFiltersForm.fetchCollectionBrandTableSection(searchParams);
    FacetFiltersForm.fetchCollectionAudienceSection(searchParams);
    FacetFiltersForm.fetchCollectionReasonsSection(searchParams);
    FacetFiltersForm.fetchCollectionFaqSection(searchParams);
    FacetFiltersForm.fetchCollectionBlogSection(searchParams);
    FacetFiltersForm.fetchCollectionRelatedModelsSection(searchParams);
    FacetFiltersForm.fetchCollectionEeatSection(searchParams);
    FacetFiltersForm.fetchPhoneFormSection(searchParams);
    FacetFiltersForm.fetchCollectionLeasingCompareSection(searchParams);
    FacetFiltersForm.fetchCollectionTrimCompareSection(searchParams);
    FacetFiltersForm.fetchCollectionModelSpecsSection(searchParams);
    FacetFiltersForm.fetchCollectionOffersSection(searchParams);
    FacetFiltersForm.fetchRichTextFilterSection(searchParams);

    BrandFilters.prototype.updateActiveFilters();
    CategoryFilters.prototype.updateActiveFilters();

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
      BrandFilters.prototype.updateActiveFilters();
      CategoryFilters.prototype.updateActiveFilters();
    }
  }

  static renderSectionFromFetch(url, event) {
    const abortController = new AbortController();
    FacetFiltersForm.currentAbortController = abortController;
    FacetFiltersForm.isRequestInProgress = true;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData.push({ html, url });
        if (FacetFiltersForm.filterData.length > 10) {
          FacetFiltersForm.filterData.shift();
        }

        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderAllSections(parsedDoc, html, event);

        if (FacetFiltersForm.loadingTimeout) {
          clearTimeout(FacetFiltersForm.loadingTimeout);
          FacetFiltersForm.loadingTimeout = null;
        }

        FacetFiltersForm.isRequestInProgress = false;
        if (FacetFiltersForm.currentAbortController === abortController) {
          FacetFiltersForm.currentAbortController = null;
        }
      })
      .catch((error) => {
        if (FacetFiltersForm.loadingTimeout) {
          clearTimeout(FacetFiltersForm.loadingTimeout);
          FacetFiltersForm.loadingTimeout = null;
        }

        const isSupersededRequest = FacetFiltersForm.currentAbortController !== abortController;

        if (!isSupersededRequest) {
          FacetFiltersForm.isRequestInProgress = false;
          FacetFiltersForm.currentAbortController = null;
        }

        if (error.name !== 'AbortError' || !isSupersededRequest) {
          FacetFiltersForm.removeLoadingState();
        }
      });
  }

  static closeFacetDisclosureOverlays() {
    document.querySelectorAll('.facets-container .disclosure-has-popup[open]').forEach((filter) => {
      if (typeof clearFacetDisclosureOpening === 'function') {
        clearFacetDisclosureOpening(filter);
      }
      filter.removeAttribute('open');
      const summary = filter.querySelector('.facets__summary, summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });

    document.querySelectorAll('details.menu-opening').forEach((details) => {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
    });
  }

  static removeLoadingState() {
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.remove('loading');
      }
    }

    const countContainer = document.getElementById('ProductCount');
    if (countContainer) {
      countContainer.classList.remove('loading');
    }

    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainerDesktop) {
      countContainerDesktop.classList.remove('loading');
    }
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderAllSections(parsedDoc, html, event);

    if (FacetFiltersForm.loadingTimeout) {
      clearTimeout(FacetFiltersForm.loadingTimeout);
      FacetFiltersForm.loadingTimeout = null;
    }
  }

  static renderAllSections(parsedDoc, html, event) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(parsedDoc);
    FacetFiltersForm.renderProductCount(parsedDoc);
    FacetFiltersForm.renderCollectionHeading(parsedDoc);
    FacetFiltersForm.renderCollectionTitle(parsedDoc);
    FacetFiltersForm.closeFacetDisclosureOverlays();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html);
  }

  static fetchSeoBrandTextSection(searchParams) {
    const anchor = document.querySelector('[data-seo-brand-section-id]');
    if (!anchor?.dataset.seoBrandSectionId) return;

    if (FacetFiltersForm.seoBrandAbortController) {
      FacetFiltersForm.seoBrandAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.seoBrandAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.seoBrandSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.seoBrandAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderSeoBrandTextSection(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.seoBrandAbortController === abortController) {
          FacetFiltersForm.seoBrandAbortController = null;
        }
      });
  }

  static scrollCollectionToolbarIntoView() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const toolbar = document.querySelector('#ProductGridContainer .collection-toolbar');
        if (!toolbar) return;
        toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  static getCollectionToolbarStickyTopPx() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    const fromVar = parseFloat(raw);
    if (Number.isFinite(fromVar) && fromVar > 0) {
      return fromVar;
    }
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader) {
      const h = sectionHeader.getBoundingClientRect().height;
      if (h > 0) return Math.round(h);
    }
    return 80;
  }

  static initCollectionToolbarStickyState() {
    if (FacetFiltersForm._collectionToolbarStickyTeardown) {
      FacetFiltersForm._collectionToolbarStickyTeardown();
      FacetFiltersForm._collectionToolbarStickyTeardown = null;
    }

    const toolbar = document.querySelector('#ProductGridContainer .collection-toolbar');
    const sentinel = document.querySelector('#ProductGridContainer .collection-toolbar-sentinel');
    if (!toolbar || !sentinel) return;

    const mq = window.matchMedia('(min-width: 990px)');
    let ticking = false;

    const update = () => {
      const t = document.querySelector('#ProductGridContainer .collection-toolbar');
      const s = document.querySelector('#ProductGridContainer .collection-toolbar-sentinel');
      if (!t || !s) return;
      if (!mq.matches) {
        t.classList.remove('collection-toolbar--stuck');
        return;
      }
      const stickyTop = FacetFiltersForm.getCollectionToolbarStickyTopPx();
      const sb = s.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      const sentinelPast = sb.bottom <= stickyTop + 1;
      const toolbarAtStickyLine = tr.top <= stickyTop + 3 && tr.top >= stickyTop - 2;
      const pinned = sentinelPast && toolbarAtStickyLine;
      t.classList.toggle('collection-toolbar--stuck', pinned);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      }
    };

    const onResize = debounce(() => update(), 150);

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    mq.addEventListener('change', update);

    FacetFiltersForm._collectionToolbarStickyTeardown = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      mq.removeEventListener('change', update);
      document
        .querySelector('#ProductGridContainer .collection-toolbar')
        ?.classList.remove('collection-toolbar--stuck');
    };
  }

  static renderCollectionTitle(parsedDoc) {
    const titleElement = parsedDoc.querySelector('.collection-page-title');
    if (titleElement) {
      const mainTitle = titleElement.innerHTML;
      const titles = document.querySelectorAll('.collection-page-title');
      titles.forEach((el) => {
        el.innerHTML = mainTitle;
      });
    }
  }

  static renderCollectionHeading(parsedDoc) {
    const headingElement = parsedDoc.getElementById('collectionHeading');
    if (!headingElement) return;
    const heading = headingElement.innerHTML;
    const collectionTitleElements = document.querySelectorAll('h1.collection-hero__title');
    if (!collectionTitleElements) return;
    collectionTitleElements.forEach((element) => {
      element.innerHTML = heading;
    });
  }

  static renderSeoBrandTextSection(parsedDoc) {
    const anchor = document.querySelector('[data-seo-brand-section-id]');
    if (!anchor) return;

    const sectionId = anchor.dataset.seoBrandSectionId;
    const newAnchor = parsedDoc.querySelector(`[data-seo-brand-section-id="${sectionId}"]`);
    if (!newAnchor) return;

    anchor.outerHTML = newAnchor.outerHTML;
  }

  static fetchCollectionSeoCardSection(searchParams) {
    const anchor = document.querySelector('[data-collection-seo-section-id]');
    if (!anchor?.dataset.collectionSeoSectionId) return;

    if (FacetFiltersForm.seoCardAbortController) {
      FacetFiltersForm.seoCardAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.seoCardAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionSeoSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.seoCardAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionSeoCard(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.seoCardAbortController === abortController) {
          FacetFiltersForm.seoCardAbortController = null;
        }
      });
  }

  static renderCollectionSeoCard(parsedDoc) {
    const current = document.querySelector('[data-collection-seo-section-id]');
    const next = parsedDoc.querySelector('[data-collection-seo-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshCollectionSeoMin === 'function') window.refreshCollectionSeoMin();
  }

  static fetchCollectionTrustSection(searchParams) {
    const anchor = document.querySelector('[data-collection-trust-section-id]');
    if (!anchor?.dataset.collectionTrustSectionId) return;

    if (FacetFiltersForm.trustAbortController) {
      FacetFiltersForm.trustAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.trustAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionTrustSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.trustAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderTrustList(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.trustAbortController === abortController) {
          FacetFiltersForm.trustAbortController = null;
        }
      });
  }

  static renderTrustList(parsedDoc) {
    const current = document.querySelector('.collection-trust-slot');
    const next = parsedDoc.querySelector('.collection-trust-slot');

    if (current && next) {
      current.replaceWith(next);
    } else if (current) {
      current.remove();
    } else if (next) {
      const anchor = document.querySelector('.facets-vertical');
      if (anchor) anchor.insertAdjacentElement('afterend', next);
    }

    if (typeof window.refreshCollectionTrustMin === 'function') {
      window.refreshCollectionTrustMin();
    }
  }

  static fetchCollectionBrandModelsSection(searchParams) {
    const anchor = document.querySelector('[data-collection-models-section-id]');
    if (!anchor?.dataset.collectionModelsSectionId) return;

    if (FacetFiltersForm.brandModelsAbortController) {
      FacetFiltersForm.brandModelsAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.brandModelsAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionModelsSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.brandModelsAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionBrandModels(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.brandModelsAbortController === abortController) {
          FacetFiltersForm.brandModelsAbortController = null;
        }
      });
  }

  static renderCollectionBrandModels(parsedDoc) {
    const current = document.querySelector('[data-collection-models-section-id]');
    const next = parsedDoc.querySelector('[data-collection-models-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshCollectionBrandModels === 'function') window.refreshCollectionBrandModels();
  }

  static fetchCollectionBrandTableSection(searchParams) {
    const anchor = document.querySelector('[data-collection-table-section-id]');
    if (!anchor?.dataset.collectionTableSectionId) return;

    if (FacetFiltersForm.brandTableAbortController) {
      FacetFiltersForm.brandTableAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.brandTableAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionTableSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.brandTableAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionBrandTable(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.brandTableAbortController === abortController) {
          FacetFiltersForm.brandTableAbortController = null;
        }
      });
  }

  static renderCollectionBrandTable(parsedDoc) {
    const current = document.querySelector('[data-collection-table-section-id]');
    const next = parsedDoc.querySelector('[data-collection-table-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshBrandTable === 'function') window.refreshBrandTable();
  }

  static fetchCollectionReasonsSection(searchParams) {
    const anchor = document.querySelector('[data-collection-reasons-section-id]');
    if (!anchor?.dataset.collectionReasonsSectionId) return;

    if (FacetFiltersForm.reasonsAbortController) {
      FacetFiltersForm.reasonsAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.reasonsAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionReasonsSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.reasonsAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionReasons(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.reasonsAbortController === abortController) {
          FacetFiltersForm.reasonsAbortController = null;
        }
      });
  }

  static renderCollectionReasons(parsedDoc) {
    const current = document.querySelector('[data-collection-reasons-section-id]');
    const next = parsedDoc.querySelector('[data-collection-reasons-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshReasonsCarousel === 'function') window.refreshReasonsCarousel();
  }

  static fetchCollectionFaqSection(searchParams) {
    const anchor = document.querySelector('[data-collection-faq-section-id]');
    if (!anchor?.dataset.collectionFaqSectionId) return;

    if (FacetFiltersForm.faqAbortController) {
      FacetFiltersForm.faqAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.faqAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionFaqSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.faqAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionFaq(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.faqAbortController === abortController) {
          FacetFiltersForm.faqAbortController = null;
        }
      });
  }

  static renderCollectionFaq(parsedDoc) {
    const current = document.querySelector('[data-collection-faq-section-id]');
    const next = parsedDoc.querySelector('[data-collection-faq-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
  }

  static fetchCollectionBlogSection(searchParams) {
    const anchors = document.querySelectorAll('[data-collection-blog-section-id]');
    if (!anchors.length) return;

    if (FacetFiltersForm.blogAbortController) {
      FacetFiltersForm.blogAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.blogAbortController = abortController;

    const requests = [...anchors].map((anchor) => {
      const sectionId = anchor.dataset.collectionBlogSectionId;
      if (!sectionId) return Promise.resolve();

      const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;

      return fetch(url, { signal: abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          if (FacetFiltersForm.blogAbortController !== abortController) return;
          const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
          FacetFiltersForm.renderCollectionBlog(parsedDoc, sectionId);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
        });
    });

    Promise.all(requests).finally(() => {
      if (FacetFiltersForm.blogAbortController === abortController) {
        FacetFiltersForm.blogAbortController = null;
      }
    });
  }

  static renderCollectionBlog(parsedDoc, sectionId) {
    const selector = `[data-collection-blog-section-id="${sectionId}"]`;
    const current = document.querySelector(selector);
    const next = parsedDoc.querySelector(selector);
    if (!current || !next) return;
    current.replaceWith(next);
  }

  static fetchCollectionRelatedModelsSection(searchParams) {
    const anchor = document.querySelector('[data-collection-related-models-section-id]');
    if (!anchor?.dataset.collectionRelatedModelsSectionId) return;

    if (FacetFiltersForm.relatedModelsAbortController) {
      FacetFiltersForm.relatedModelsAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.relatedModelsAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionRelatedModelsSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.relatedModelsAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionRelatedModels(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.relatedModelsAbortController === abortController) {
          FacetFiltersForm.relatedModelsAbortController = null;
        }
      });
  }

  static renderCollectionRelatedModels(parsedDoc) {
    const current = document.querySelector('[data-collection-related-models-section-id]');
    const next = parsedDoc.querySelector('[data-collection-related-models-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshRelatedModels === 'function') window.refreshRelatedModels();
  }

  static fetchCollectionEeatSection(searchParams) {
    const anchor = document.querySelector('[data-collection-eeat-section-id]');
    if (!anchor?.dataset.collectionEeatSectionId) return;

    if (FacetFiltersForm.eeatAbortController) {
      FacetFiltersForm.eeatAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.eeatAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionEeatSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.eeatAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionEeat(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.eeatAbortController === abortController) {
          FacetFiltersForm.eeatAbortController = null;
        }
      });
  }

  static renderCollectionEeat(parsedDoc) {
    const current = document.querySelector('[data-collection-eeat-section-id]');
    const next = parsedDoc.querySelector('[data-collection-eeat-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
  }

  static fetchPhoneFormSection(searchParams) {
    const anchor = document.querySelector('[data-phone-form-section-id]');
    if (!anchor?.dataset.phoneFormSectionId) return;

    if (FacetFiltersForm.phoneFormAbortController) {
      FacetFiltersForm.phoneFormAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.phoneFormAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.phoneFormSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.phoneFormAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderPhoneFormSection(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.phoneFormAbortController === abortController) {
          FacetFiltersForm.phoneFormAbortController = null;
        }
      });
  }

  static renderPhoneFormSection(parsedDoc) {
    const current = document.querySelector('[data-phone-form-section-id]');
    const next = parsedDoc.querySelector('[data-phone-form-section-id]');
    if (!current || !next) return;
    const currentSection = current.closest('.shopify-section');
    const nextSection = next.closest('.shopify-section');
    if (currentSection && nextSection) {
      currentSection.replaceWith(nextSection);
      return;
    }
    current.replaceWith(next);
  }

  static fetchCollectionLeasingCompareSection(searchParams) {
    const anchor = document.querySelector('[data-collection-leasing-compare-section-id]');
    if (!anchor?.dataset.collectionLeasingCompareSectionId) return;

    if (FacetFiltersForm.leasingCompareAbortController) {
      FacetFiltersForm.leasingCompareAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.leasingCompareAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionLeasingCompareSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.leasingCompareAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionLeasingCompare(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.leasingCompareAbortController === abortController) {
          FacetFiltersForm.leasingCompareAbortController = null;
        }
      });
  }

  static renderCollectionLeasingCompare(parsedDoc) {
    const current = document.querySelector('[data-collection-leasing-compare-section-id]');
    const next = parsedDoc.querySelector('[data-collection-leasing-compare-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
  }

  static fetchCollectionTrimCompareSection(searchParams) {
    const anchor = document.querySelector('[data-collection-trim-compare-section-id]');
    if (!anchor?.dataset.collectionTrimCompareSectionId) return;

    if (FacetFiltersForm.trimCompareAbortController) {
      FacetFiltersForm.trimCompareAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.trimCompareAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionTrimCompareSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.trimCompareAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionTrimCompare(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.trimCompareAbortController === abortController) {
          FacetFiltersForm.trimCompareAbortController = null;
        }
      });
  }

  static renderCollectionTrimCompare(parsedDoc) {
    const current = document.querySelector('[data-collection-trim-compare-section-id]');
    const next = parsedDoc.querySelector('[data-collection-trim-compare-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshTrimCompare === 'function') window.refreshTrimCompare();
  }

  static fetchCollectionModelSpecsSection(searchParams) {
    const anchor = document.querySelector('[data-collection-model-specs-section-id]');
    if (!anchor?.dataset.collectionModelSpecsSectionId) return;

    if (FacetFiltersForm.modelSpecsAbortController) {
      FacetFiltersForm.modelSpecsAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.modelSpecsAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionModelSpecsSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.modelSpecsAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionModelSpecs(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.modelSpecsAbortController === abortController) {
          FacetFiltersForm.modelSpecsAbortController = null;
        }
      });
  }

  static renderCollectionModelSpecs(parsedDoc) {
    const current = document.querySelector('[data-collection-model-specs-section-id]');
    const next = parsedDoc.querySelector('[data-collection-model-specs-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
  }

  static fetchRichTextFilterSection(searchParams) {
    const anchor = document.querySelector('[data-rich-text-filter-section-id]');
    if (!anchor?.dataset.richTextFilterSectionId) return;

    if (FacetFiltersForm.richTextFilterAbortController) {
      FacetFiltersForm.richTextFilterAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.richTextFilterAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.richTextFilterSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.richTextFilterAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderRichTextFilterSection(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.richTextFilterAbortController === abortController) {
          FacetFiltersForm.richTextFilterAbortController = null;
        }
      });
  }

  static renderRichTextFilterSection(parsedDoc) {
    const current = document.querySelector('[data-rich-text-filter-section-id]');
    const next = parsedDoc.querySelector('[data-rich-text-filter-section-id]');
    if (!current || !next) return;
    const currentSection = current.closest('.shopify-section');
    const nextSection = next.closest('.shopify-section');
    if (currentSection && nextSection) {
      currentSection.replaceWith(nextSection);
      return;
    }
    current.replaceWith(next);
  }

  static fetchCollectionAudienceSection(searchParams) {
    const anchor = document.querySelector('[data-collection-audience-section-id]');
    if (!anchor?.dataset.collectionAudienceSectionId) return;

    if (FacetFiltersForm.audienceAbortController) {
      FacetFiltersForm.audienceAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.audienceAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionAudienceSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.audienceAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionAudience(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.audienceAbortController === abortController) {
          FacetFiltersForm.audienceAbortController = null;
        }
      });
  }

  static renderCollectionAudience(parsedDoc) {
    const current = document.querySelector('[data-collection-audience-section-id]');
    const next = parsedDoc.querySelector('[data-collection-audience-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
  }

  static fetchCollectionOffersSection(searchParams) {
    const anchor = document.querySelector('[data-collection-offers-section-id]');
    if (!anchor?.dataset.collectionOffersSectionId) return;

    if (FacetFiltersForm.offersAbortController) {
      FacetFiltersForm.offersAbortController.abort();
    }

    const abortController = new AbortController();
    FacetFiltersForm.offersAbortController = abortController;

    const url = `${window.location.pathname}?section_id=${anchor.dataset.collectionOffersSectionId}&${searchParams}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.offersAbortController !== abortController) return;
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderCollectionOffers(parsedDoc);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
      })
      .finally(() => {
        if (FacetFiltersForm.offersAbortController === abortController) {
          FacetFiltersForm.offersAbortController = null;
        }
      });
  }

  static renderCollectionOffers(parsedDoc) {
    const current = document.querySelector('[data-collection-offers-section-id]');
    const next = parsedDoc.querySelector('[data-collection-offers-section-id]');
    if (!current || !next) return;
    current.replaceWith(next);
    if (typeof window.refreshCollectionOffers === 'function') window.refreshCollectionOffers();
  }

  static renderProductGridContainer(parsedDoc) {
    const wrapper = document.querySelector('.product-grid-container-wrapper');
    const newWrapper = parsedDoc.querySelector('.product-grid-container-wrapper');
    if (newWrapper && wrapper) {
      wrapper.innerHTML = newWrapper.innerHTML;
    } else {
      const newGridContainer = parsedDoc.getElementById('ProductGridContainer');
      if (!newGridContainer) return;
      document.getElementById('ProductGridContainer').innerHTML = newGridContainer.innerHTML;
    }

    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) {
      collection.classList.remove('loading');
    }

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      productGridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    }

    FacetFiltersForm.resortProductsByAvailablePrice();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        FacetFiltersForm.initCollectionToolbarStickyState();
      });
    });
  }

  static resortProductsByAvailablePrice() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');

    if (!sortBy || (sortBy !== 'price-ascending' && sortBy !== 'price-descending')) {
      return;
    }

    const productGrid = document.querySelector('#ProductGridContainer .collection ul');
    if (!productGrid) return;

    const allCards = Array.from(productGrid.children).filter((child) => child.tagName === 'LI');
    const productCardsWithData = [];
    const promoCardsWithPosition = [];

    allCards.forEach((card, index) => {
      const wrapper = card.querySelector('.card-wrapper[data-available-price]');

      if (wrapper) {
        const price = parseInt(wrapper.getAttribute('data-available-price'), 10);
        productCardsWithData.push({ card, price });
      } else {
        promoCardsWithPosition.push({ card, position: index });
      }
    });

    if (productCardsWithData.length === 0) return;

    productCardsWithData.sort((a, b) => {
      return sortBy === 'price-ascending' ? a.price - b.price : b.price - a.price;
    });

    const sortedProducts = productCardsWithData.map((item) => item.card);
    const finalOrder = [...sortedProducts];

    promoCardsWithPosition.forEach(({ card, position }) => {
      finalOrder.splice(position, 0, card);
    });

    const fragment = document.createDocumentFragment();
    finalOrder.forEach((card) => {
      fragment.appendChild(card);
    });

    productGrid.innerHTML = '';
    productGrid.appendChild(fragment);
  }

  static updateFacetsCloseButton(count) {
    const facetsCloseButton = document.getElementById('facetsCloseButton');
    if (!facetsCloseButton) return;

    let text;
    if (count === 1) {
      text = 'Zobrazit 1 nabídku';
    } else if (count >= 2 && count <= 4) {
      text = `Zobrazit ${count} nabídky`;
    } else {
      text = `Zobrazit ${count} nabídek`;
    }

    facetsCloseButton.textContent = text;
  }

  static extractProductCount(text) {
    if (!text) return null;

    const textContent = text.replace(/<[^>]*>/g, '').trim();

    if (textContent.includes(' z ')) {
      const match = textContent.match(/^(\d+)\s+z\s+\d+/);
      if (match) {
        return parseInt(match[1], 10);
      }
    }

    const matches = textContent.match(/(\d+)/g);
    if (matches && matches.length > 0) {
      return parseInt(matches[matches.length - 1], 10);
    }

    return null;
  }

  static renderProductCount(parsedDoc) {
    const countElement = parsedDoc.getElementById('ProductCount');
    if (!countElement) return;
    const count = countElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    const parsedButton = parsedDoc.getElementById('facetsCloseButton');
    let productCount = null;

    if (parsedButton && parsedButton.getAttribute('data-product-count')) {
      productCount = parseInt(parsedButton.getAttribute('data-product-count'), 10);
    } else {
      const currentButton = document.getElementById('facetsCloseButton');
      if (currentButton && currentButton.getAttribute('data-product-count')) {
        productCount = parseInt(currentButton.getAttribute('data-product-count'), 10);
      } else {
        productCount = FacetFiltersForm.extractProductCount(count);
      }
    }

    if (productCount !== null && !isNaN(productCount)) {
      FacetFiltersForm.updateFacetsCloseButton(productCount);
    }

    BrandFilters.prototype.updateProductCount();

    const categoryFiltersElements = document.querySelectorAll('category-filters');
    categoryFiltersElements.forEach((element) => {
      if (element && element.updateProductCount) {
        element.updateProductCount();
      }
    });
  }

  static renderFilters(html, event) {
    suppressFacetEnterAnimations();
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm, #FacetFiltersPillsForm');
    const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm, #FacetFiltersPillsForm');

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    let jsFilterContext = null;
    if (event && event.target && typeof event.target.closest === 'function') {
      jsFilterContext = event.target.closest('.js-filter');
    }

    const matchesId = (elementToMatch) => {
      return jsFilterContext ? elementToMatch.id === jsFilterContext.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const referenceNode = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (referenceNode) {
            referenceNode.before(elementToRender);
          } else if (document.querySelector(`#${elementToRender.parentElement.id}`)) {
            document.querySelector(`#${elementToRender.parentElement.id}`).appendChild(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.toggleMostSearched(parsedHTML);

    document.querySelectorAll('facet-filters-form').forEach((form) => {
      form.setupCustomDropdowns();
      FacetFiltersForm.organizeAdvancedFilters(form);
    });

    if (countsToRender && jsFilterContext) {
      const closestJSFilterID = jsFilterContext.id;

      FacetFiltersForm.renderCounts(countsToRender, jsFilterContext);

      const mobileCountsTargetEl = document.getElementById(closestJSFilterID);
      if (mobileCountsTargetEl) {
        FacetFiltersForm.renderMobileCounts(countsToRender, mobileCountsTargetEl);
      }

      const newFacetDetailsElement = document.getElementById(closestJSFilterID);
      if (newFacetDetailsElement) {
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event && event.target && event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) {
          newElementToActivate.focus({ preventScroll: true });
        }
      }
    }

    releaseFacetEnterAnimations();
  }

  static organizeAdvancedFilters(root = document) {
    const forms = root.matches?.('form') ? [root] : Array.from(root.querySelectorAll('form'));

    forms.forEach((form) => {
      const existingDetails = form.querySelector('.facets-advanced');
      if (existingDetails) {
        const content = existingDetails.querySelector('.facets-advanced__content');
        if (content) {
          Array.from(content.children).forEach((filter) => existingDetails.before(filter));
        }
        existingDetails.remove();
      }

      applyStoredAdvancedFacetOpenState(form);

      const collapsibleFilters = Array.from(form.querySelectorAll('.js-filter'));
      if (collapsibleFilters.length === 0) return;

      collapsibleFilters.forEach((filter) => {
        const summary = filter.querySelector('.facets__summary');
        if (!summary) return;

        const summaryLabel = summary.querySelector('.facets__summary-label, .body-title-m');
        const dropdownPlaceholder = summary.querySelector('.dropdown-trigger .dropdown-label-text');
        if (summaryLabel && dropdownPlaceholder && summaryLabel.textContent.trim() === '') {
          summaryLabel.textContent = dropdownPlaceholder.textContent.trim();
        }

        summary.setAttribute('role', 'button');
        summary.setAttribute('tabindex', '0');
        summary.setAttribute('aria-expanded', filter.hasAttribute('open') ? 'true' : 'false');

        if (filter.dataset.filterDisclosureBound === 'true') return;
        filter.dataset.filterDisclosureBound = 'true';

        const toggleFilter = () => {
          const nextOpen = !filter.hasAttribute('open');
          if (nextOpen) {
            markFacetDisclosureOpening(filter);
            filter.setAttribute('open', '');
          } else {
            clearFacetDisclosureOpening(filter);
            filter.removeAttribute('open');
          }
          summary.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
          persistAdvancedFacetOpenState(filter, nextOpen);
        };

        summary.addEventListener('click', (event) => {
          if (event.target.closest('custom-dropdown, button, a, input, label, select, textarea')) return;
          toggleFilter();
        });

        summary.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggleFilter();
        });
      });
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const sources = html.querySelectorAll(selector);
      const targets = document.querySelectorAll(selector);
      sources.forEach((source, index) => {
        if (targets[index]) {
          targets[index].innerHTML = source.innerHTML;
        }
      });
    });

    const parsedToolbarFilters = html.querySelector('#ProductGridContainer .active-filters');
    const toolbarFilters = document.querySelector('#ProductGridContainer .active-filters');
    if (parsedToolbarFilters && toolbarFilters) {
      toolbarFilters.innerHTML = parsedToolbarFilters.innerHTML;
    }

    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.toggleMostSearched(html);
  }

  static toggleMostSearched(parsedHTML) {
    const mostSearchedElement = document.querySelector('.most-searched');
    if (!mostSearchedElement) return;

    const urlParams = new URLSearchParams(window.location.search);
    const hasFilterParams = Array.from(urlParams.keys()).some((key) => key.startsWith('filter.'));

    const activeFiltersContent = document.querySelector('.active-filters-content');
    const activeFacetsDesktop = document.querySelector('.active-facets.active-facets-desktop');
    const hasActiveFiltersInDOM =
      (activeFiltersContent && activeFiltersContent.querySelectorAll('facet-remove').length > 0) ||
      (activeFacetsDesktop && activeFacetsDesktop.querySelectorAll('facet-remove, .active-facets__button').length > 0);

    if (hasFilterParams || hasActiveFiltersInDOM) {
      mostSearchedElement.style.display = 'none';
    } else {
      mostSearchedElement.style.display = '';
    }
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;
      targetElement.innerHTML = html.querySelector(selector).innerHTML;
    });

    const parsedButton = html.getElementById('facetsCloseButton');
    const currentButton = document.getElementById('facetsCloseButton');
    if (parsedButton && currentButton) {
      const productCount = parsedButton.getAttribute('data-product-count');
      if (productCount) {
        currentButton.setAttribute('data-product-count', productCount);
        const count = parseInt(productCount, 10);
        if (!isNaN(count)) {
          FacetFiltersForm.updateFacetsCloseButton(count);
        }
      }
    }

    const menuDrawer =
      document.getElementById('facet-form-mount-mobile')?.closest('menu-drawer') ||
      document.querySelector('.facets-unified-root menu-drawer');
    if (menuDrawer && menuDrawer.bindEvents) {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      if (!targetWrapElement.hasAttribute('data-facet-tier-root')) {
        const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
        if (isShowingMore) {
          sourceWrapElement
            .querySelectorAll('.facets__item.hidden')
            .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
        }
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
      window.FacetTierShowMore?.syncAll?.(document);
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const hash = window.location.hash || '';
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}${hash}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();

    const params = {};

    const addParamValue = (key, value) => {
      if (!params[key]) {
        params[key] = new Set();
      }
      params[key].add(value);
    };

    const currentUrl = new URLSearchParams(window.location.search);
    const currentSortBy = currentUrl.get('sort_by');
    if (currentSortBy && currentSortBy !== '') {
      searchParams.append('sort_by', currentSortBy);
    }

    const priceGte = formData.get('filter.v.price.gte');
    const priceLte = formData.get('filter.v.price.lte');

    const priceRangeElement = form.querySelector('price-range');
    let includePrice = false;

    if (priceRangeElement) {
      const lowerRange = priceRangeElement.querySelector('.lower-range');
      const upperRange = priceRangeElement.querySelector('.upper-range');

      if (lowerRange && upperRange) {
        const rangeMinValue = parseFloat(lowerRange.min) * 100;
        const rangeMaxValue = parseFloat(upperRange.max) * 100;
        const currentMinValue = parseFloat(lowerRange.value) * 100;
        const currentMaxValue = parseFloat(upperRange.value) * 100;

        includePrice = currentMinValue > rangeMinValue || currentMaxValue < rangeMaxValue;
      }
    }

    if (includePrice && priceGte !== '' && priceGte !== null) {
      searchParams.append('filter.v.price.gte', priceGte);
    }
    if (includePrice && priceLte !== '' && priceLte !== null) {
      searchParams.append('filter.v.price.lte', priceLte);
    }

    for (const [key, value] of formData.entries()) {
      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte' || key === 'sort_by' || value === '') {
        continue;
      }
      const inputElement = form.querySelector(`input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`);
      if (inputElement && inputElement.type === 'checkbox' && !inputElement.checked) {
        continue;
      }
      addParamValue(key, value);
    }

    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        params[key].forEach((uniqueValue) => {
          searchParams.append(key, uniqueValue);
        });
      }
    }

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event, true);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
    FacetFiltersForm.updateURLHash(url);

    BrandFilters.prototype.updateActiveFilters();
    CategoryFilters.prototype.updateActiveFilters();
  }

  setupCustomDropdowns() {
    const updateDropdownDisplay = (dropdown) => {
      const selectedValueSpan = dropdown.querySelector('.selected-value');
      const defaultSpan = dropdown.querySelector('.dropdown-trigger > span:not(.selected-value)');
      const checkedInputs = dropdown.querySelectorAll('input[type="checkbox"]:checked');

      if (checkedInputs.length > 0) {
        const selectedLabels = Array.from(checkedInputs).map((input) => {
          const text = input.closest('label').querySelector('.dropdown-text');
          return text ? text.textContent.replace(/\s+\d+$/, '') : input.value;
        });
        selectedValueSpan.textContent = selectedLabels.join(', ');
        defaultSpan.style.display = 'none';
      } else {
        selectedValueSpan.textContent = '';
        defaultSpan.style.display = 'inline';
      }
    };

    const hideZeroCountItems = (dropdown) => {
      const items = dropdown.querySelectorAll('.dropdown-item');
      items.forEach((item) => {
        const countSpan = item.querySelector('.js-original-count');
        if (countSpan) {
          const match = countSpan.textContent.trim().match(/\d+/);
          const count = parseInt(match ? match[0] : '0');
          const checkbox = item.querySelector('input[type="checkbox"]');

          if (count === 0) {
            item.classList.add('hidden');
            if (checkbox) checkbox.disabled = true;
          } else {
            item.classList.remove('hidden');
            if (checkbox) checkbox.disabled = false;
          }
        }
      });
    };

    this.querySelectorAll('custom-dropdown:not(.sorting-dropdown)').forEach((dropdown) => {
      updateDropdownDisplay(dropdown);
      hideZeroCountItems(dropdown);

      const trigger = dropdown.querySelector('.dropdown-trigger');
      if (trigger && dropdown.dataset.facetScrollBound !== 'true') {
        dropdown.dataset.facetScrollBound = 'true';
        trigger.addEventListener('click', () => {
          requestAnimationFrame(() => {
            if (trigger.getAttribute('aria-expanded') !== 'true') return;
            const filter = dropdown.closest('.js-filter');
            const form = this.querySelector('form');
            if (!filter || !form) return;

            const dropdownContent = dropdown.querySelector('.dropdown-content');
            const runScroll = () => scrollFacetDropdownIntoView(filter, form);

            if (!dropdownContent || !filter.classList.contains('facets__display--dropdown')) {
              runScroll();
              return;
            }

            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (reducedMotion) {
              runScroll();
              return;
            }

            let scrolled = false;
            const scrollOnce = () => {
              if (scrolled) return;
              scrolled = true;
              runScroll();
            };

            dropdownContent.addEventListener('transitionend', scrollOnce, { once: true });
            window.setTimeout(scrollOnce, 350);
          });
        });
      }

      dropdown.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', () => {
          updateDropdownDisplay(dropdown);
        });
      });
    });

    this.querySelectorAll('custom-dropdown.sorting-dropdown').forEach((dropdown) => {
      updateDropdownDisplay(dropdown);

      dropdown.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', () => {
          updateDropdownDisplay(dropdown);
        });
      });
    });

    this.querySelectorAll('.facets__item').forEach((item) => {
      const countSpan = item.querySelector('.js-original-count, .facet-count');
      if (countSpan) {
        const match = countSpan.textContent.trim().match(/\d+/);
        const count = parseInt(match ? match[0] : '0');
        const checkbox = item.querySelector('input[type="checkbox"]');
        const label = item.querySelector('.facets__label');

        if (count === 0) {
          item.classList.add('hidden');
          if (checkbox) checkbox.disabled = true;
          if (label) label.classList.add('disabled');
        } else {
          item.classList.remove('hidden');
          if (checkbox) checkbox.disabled = false;
          if (label) label.classList.remove('disabled');
        }
      }
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.currentAbortController = null;
FacetFiltersForm.isRequestInProgress = false;
FacetFiltersForm.loadingTimeout = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.lowerRange = this.querySelector('.lower-range');
    this.upperRange = this.querySelector('.upper-range');
    this.lowerBubble = this.querySelector('.lower-bubble');
    this.upperBubble = this.querySelector('.upper-bubble');
    this.rangeSelected = this.querySelector('.range-selected');
    this.stepMarksContainer = this.querySelector('.step-marks');
    this.rangeWrap = this.querySelector('.range-wrap');

    if (!this.lowerRange || !this.upperRange) return;

    const originalLowerValue = Math.floor(parseFloat(this.lowerRange.value) / 100);
    const originalUpperValue = Math.floor(parseFloat(this.upperRange.value) / 100);

    this.lowerRange.max = Math.floor(parseFloat(this.lowerRange.max) / 100);
    this.upperRange.max = Math.floor(parseFloat(this.upperRange.max) / 100);

    const maxDisplayValue = Math.floor(parseFloat(this.upperRange.max));
    const stepSize = Math.floor(maxDisplayValue / 20);

    this.lowerRange.step = stepSize || 1;
    this.upperRange.step = stepSize || 1;

    this.lowerRange.value = originalLowerValue;

    if (originalUpperValue >= maxDisplayValue - 50) {
      this.upperRange.step = 1;
      this.upperRange.value = maxDisplayValue;
    } else {
      this.upperRange.value = originalUpperValue;
    }

    this.createStepMarks();

    this.onRangeChange = this.onRangeChange.bind(this);
    this.onRangeChangeEnd = this.onRangeChangeEnd.bind(this);
    this.updateZIndex = this.updateZIndex.bind(this);
    this.debouncedOnRangeChangeEnd = debounce(this.onRangeChangeEnd, 400);

    this.lowerRange.style.zIndex = '3';
    this.upperRange.style.zIndex = '4';

    if (this.rangeWrap) {
      this.rangeWrap.addEventListener('pointermove', this.updateZIndex);
      this.rangeWrap.addEventListener('pointerenter', this.updateZIndex);
    }

    [this.lowerRange, this.upperRange].forEach((range) => {
      range.addEventListener('input', this.onRangeChange);
      range.addEventListener('change', this.debouncedOnRangeChangeEnd);
    });

    this.updateUI();
  }

  updateZIndex(event) {
    const lowerValue = parseFloat(this.lowerRange.value);
    const upperValue = parseFloat(this.upperRange.value);

    const rect = this.rangeWrap.getBoundingClientRect();
    const clientX = event.clientX;
    const mouseX = clientX - rect.left;
    const mousePercent = (mouseX / rect.width) * 100;

    const min = parseFloat(this.lowerRange.min);
    const max = parseFloat(this.lowerRange.max);
    const lowerPercent = ((lowerValue - min) / (max - min)) * 100;
    const upperPercent = ((upperValue - min) / (max - min)) * 100;

    if (lowerValue === upperValue) {
      if (mousePercent < lowerPercent) {
        this.lowerRange.style.zIndex = '5';
        this.upperRange.style.zIndex = '4';
      } else {
        this.lowerRange.style.zIndex = '3';
        this.upperRange.style.zIndex = '5';
      }
    } else {
      const distToLower = Math.abs(mousePercent - lowerPercent);
      const distToUpper = Math.abs(mousePercent - upperPercent);

      if (distToLower < distToUpper) {
        this.lowerRange.style.zIndex = '5';
        this.upperRange.style.zIndex = '4';
      } else {
        this.lowerRange.style.zIndex = '4';
        this.upperRange.style.zIndex = '5';
      }
    }
  }

  createStepMarks() {
    if (!this.stepMarksContainer) return;

    const numberOfMarks = 12;
    const maxValue = parseFloat(this.upperRange.getAttribute('max'));
    const stepValue = Math.floor(maxValue / numberOfMarks);

    for (let i = 1; i <= numberOfMarks; i++) {
      const mark = document.createElement('div');
      mark.className = 'step-mark';
      mark.dataset.value = i === numberOfMarks ? maxValue : stepValue * i;
      this.stepMarksContainer.appendChild(mark);
    }
  }

  onRangeChange(event) {
    const isLower = event.target === this.lowerRange;
    const isUpper = event.target === this.upperRange;
    let lowerValue = parseFloat(this.lowerRange.value);
    let upperValue = parseFloat(this.upperRange.value);
    const maxValue = parseFloat(this.upperRange.max);
    const minValue = parseFloat(this.lowerRange.min);
    const step = parseFloat(this.upperRange.step) || 1;

    if (isLower && lowerValue > upperValue) {
      this.lowerRange.value = upperValue;
      lowerValue = upperValue;
    }

    if (isUpper && upperValue < lowerValue) {
      this.upperRange.value = lowerValue;
      upperValue = lowerValue;
    }

    if (isUpper) {
      const maxPossibleStepValue = Math.floor(maxValue / step) * step;

      if (upperValue > maxPossibleStepValue) {
        this.upperRange.value = maxValue;
        upperValue = maxValue;
      } else if (upperValue >= maxValue - step) {
        this.upperRange.value = maxValue;
        upperValue = maxValue;
      }
    }

    this.updateUI();
  }

  onRangeChangeEnd() {
    const form = this.closest('form');
    if (form) {
      const lowerValue = parseFloat(this.lowerRange.value);
      let upperValue = parseFloat(this.upperRange.value);
      const maxValue = parseFloat(this.upperRange.max);

      const step = parseFloat(this.upperRange.step) || 1;
      const maxPossibleStepValue = Math.floor(maxValue / step) * step;

      if (upperValue >= maxPossibleStepValue || upperValue >= maxValue - step) {
        upperValue = maxValue;
        this.upperRange.value = maxValue;
      }

      const originalLowerValue = this.lowerRange.value;
      const originalUpperValue = this.upperRange.value;

      this.lowerRange.value = lowerValue * 100;
      this.upperRange.value = upperValue * 100;

      const priceChangeEvent = new Event('input', {
        bubbles: true,
      });
      priceChangeEvent.isPriceRangeEvent = true;
      form.dispatchEvent(priceChangeEvent);

      this.lowerRange.value = originalLowerValue;
      this.upperRange.value = originalUpperValue;
    }
  }

  formatPrice(value) {
    const originalMax = parseFloat(this.upperRange.getAttribute('max'));
    const step = parseFloat(this.upperRange.step) || 1;
    const maxPossibleStepValue = Math.floor(originalMax / step) * step;

    if (value >= maxPossibleStepValue) {
      return new Intl.NumberFormat('cs-CZ', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(originalMax);
    }

    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    }).format(value);
  }

  updateUI() {
    const min = parseFloat(this.lowerRange.min);
    const max = parseFloat(this.lowerRange.max);
    const lowerValue = parseFloat(this.lowerRange.value);
    const upperValue = parseFloat(this.upperRange.value);

    const lowerPercent = ((lowerValue - min) / (max - min)) * 100;
    const upperPercent = ((upperValue - min) / (max - min)) * 100;

    this.lowerBubble.textContent = this.formatPrice(lowerValue);
    this.upperBubble.textContent = this.formatPrice(upperValue);

    this.lowerBubble.style.left = `calc(${lowerPercent}% + (${12 - lowerPercent * 0.24}px))`;
    this.upperBubble.style.right = `calc(${100 - upperPercent}% + (${12 - (100 - upperPercent) * 0.24}px))`;

    this.rangeSelected.style.left = `${lowerPercent}%`;
    this.rangeSelected.style.right = `${100 - upperPercent}%`;
  }
}

customElements.define('price-range', PriceRange);

class FacetListRange extends HTMLElement {
  constructor() {
    super();
    this.lowerRange = this.querySelector('.lower-range');
    this.upperRange = this.querySelector('.upper-range');
    this.lowerBubble = this.querySelector('.lower-bubble');
    this.upperBubble = this.querySelector('.upper-bubble');
    this.rangeSelected = this.querySelector('.range-selected');
    this.rangeWrap = this.querySelector('.range-wrap');
    this.checkboxes = this.querySelectorAll('.facet-list-range-checkbox');
    if (!this.lowerRange || !this.upperRange || !this.checkboxes.length) return;
    this.maxIndex = parseInt(this.dataset.maxIndex || '0', 10);
    this.labels = Array.from(this.checkboxes).map((cb) => cb.dataset.label || '');
    this.onRangeChange = this.onRangeChange.bind(this);
    this.onRangeChangeEnd = this.onRangeChangeEnd.bind(this);
    this.updateZIndex = this.updateZIndex.bind(this);
    this.debouncedOnRangeChangeEnd = debounce(this.onRangeChangeEnd, 400);

    this.lowerRange.style.zIndex = '3';
    this.upperRange.style.zIndex = '4';

    if (this.rangeWrap) {
      this.rangeWrap.addEventListener('pointermove', this.updateZIndex);
      this.rangeWrap.addEventListener('pointerenter', this.updateZIndex);
    }

    [this.lowerRange, this.upperRange].forEach((range) => {
      range.addEventListener('input', this.onRangeChange);
      range.addEventListener('change', this.debouncedOnRangeChangeEnd);
    });
    let minChecked = this.maxIndex + 1;
    let maxChecked = -1;
    this.checkboxes.forEach((cb, i) => {
      if (cb.checked) {
        if (i < minChecked) minChecked = i;
        if (i > maxChecked) maxChecked = i;
      }
    });
    if (maxChecked >= 0) {
      this.lowerRange.value = minChecked;
      this.upperRange.value = maxChecked;
    } else {
      this.lowerRange.value = 0;
      this.upperRange.value = this.maxIndex;
    }
    this.updateUI();
  }

  updateZIndex(event) {
    const lowerValue = parseInt(this.lowerRange.value, 10);
    const upperValue = parseInt(this.upperRange.value, 10);

    const rect = this.rangeWrap.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mousePercent = (mouseX / rect.width) * 100;

    const lowerPercent = (lowerValue / this.maxIndex) * 100;
    const upperPercent = (upperValue / this.maxIndex) * 100;

    if (lowerValue === upperValue) {
      if (mousePercent < lowerPercent) {
        this.lowerRange.style.zIndex = '5';
        this.upperRange.style.zIndex = '4';
      } else {
        this.lowerRange.style.zIndex = '3';
        this.upperRange.style.zIndex = '5';
      }
    } else {
      const distToLower = Math.abs(mousePercent - lowerPercent);
      const distToUpper = Math.abs(mousePercent - upperPercent);

      if (distToLower < distToUpper) {
        this.lowerRange.style.zIndex = '5';
        this.upperRange.style.zIndex = '4';
      } else {
        this.lowerRange.style.zIndex = '4';
        this.upperRange.style.zIndex = '5';
      }
    }
  }

  onRangeChange(event) {
    const isLower = event.target === this.lowerRange;
    const isUpper = event.target === this.upperRange;
    let lowerValue = parseInt(this.lowerRange.value, 10);
    let upperValue = parseInt(this.upperRange.value, 10);

    if (isLower && lowerValue > upperValue) {
      this.lowerRange.value = upperValue;
      lowerValue = upperValue;
    }
    if (isUpper && upperValue < lowerValue) {
      this.upperRange.value = lowerValue;
      upperValue = lowerValue;
    }

    this.syncCheckboxesFromRanges();
    this.updateUI();
  }

  syncCheckboxesFromRanges() {
    const lower = parseInt(this.lowerRange.value, 10);
    const upper = parseInt(this.upperRange.value, 10);
    const fullRange = lower === 0 && upper === this.maxIndex;
    this.checkboxes.forEach((cb) => {
      const idx = parseInt(cb.dataset.valueIndex, 10);
      cb.checked = !fullRange && idx >= lower && idx <= upper;
    });
  }

  formatBubbleLabel(raw) {
    if (!raw) return '';
    let s = String(raw)
      .replace(/^\s*(od|do)\s+/i, '')
      .replace(/\s*(km|měsíců|měsíce|měsíc)\s*$/i, '')
      .trim();
    return s;
  }

  updateUI() {
    const max = this.maxIndex || 0;
    const lowerValue = parseInt(this.lowerRange.value, 10);
    const upperValue = parseInt(this.upperRange.value, 10);
    if (max <= 0) return;
    const lowerPercent = (lowerValue / max) * 100;
    const upperPercent = (upperValue / max) * 100;
    if (this.lowerBubble) this.lowerBubble.textContent = this.formatBubbleLabel(this.labels[lowerValue]) || lowerValue;
    if (this.upperBubble) this.upperBubble.textContent = this.formatBubbleLabel(this.labels[upperValue]) || upperValue;
    if (this.lowerBubble) {
      this.lowerBubble.style.left = `calc(${lowerPercent}% + (${12 - lowerPercent * 0.24}px))`;
    }
    if (this.upperBubble) {
      this.upperBubble.style.right = `calc(${100 - upperPercent}% + (${12 - (100 - upperPercent) * 0.24}px))`;
    }
    if (this.rangeSelected) {
      this.rangeSelected.style.left = `${lowerPercent}%`;
      this.rangeSelected.style.right = `${100 - upperPercent}%`;
    }
  }

  onRangeChangeEnd() {
    const form = this.closest('form');
    if (form) {
      form.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

customElements.define('facet-list-range', FacetListRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);

    requestAnimationFrame(() => {
      document.querySelectorAll('facet-filters-form').forEach((form) => {
        form.setupCustomDropdowns();
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

const facetsToggle = document.querySelector(
  '.facets-container .mobile-facets-toggle:not(.mobile-facets-sort-bar__filter-btn)',
);
const facetsClose = document.querySelector('.facets-close');
const facetsCloseButton = document.getElementById('facetsCloseButton');

function getFiltersMenuDrawer() {
  return (
    document.getElementById('facet-form-mount-mobile')?.closest('menu-drawer') ||
    document.querySelector('.facets-unified-root menu-drawer') ||
    document.querySelector('menu-drawer')
  );
}

function openFiltersDrawer() {
  const menuDrawer = getFiltersMenuDrawer();
  if (menuDrawer) {
    menuDrawer.classList.add('is-open');
    document.body.classList.add('filters-open');
    // One-shot enter animation when opening the filter menu (not on later AJAX refreshes).
    menuDrawer.querySelectorAll('.js-filter[open]').forEach((filter) => {
      markFacetDisclosureOpening(filter);
    });
  }
}

if (facetsToggle) {
  facetsToggle.addEventListener('click', openFiltersDrawer);
}

document.addEventListener('click', function (e) {
  if (e.target.closest('[data-open-filters-drawer]')) {
    e.preventDefault();
    openFiltersDrawer();
  }
});

if (facetsClose) {
  facetsClose.addEventListener('click', function () {
    const menuDrawer = this.closest('menu-drawer');
    if (menuDrawer) {
      menuDrawer.classList.remove('is-open');
      document.body.classList.remove('filters-open');
    }
  });
}

if (facetsCloseButton) {
  facetsCloseButton.addEventListener('click', function () {
    const menuDrawer = this.closest('menu-drawer');
    if (menuDrawer) {
      menuDrawer.classList.remove('is-open');
      document.body.classList.remove('filters-open');
    }
  });
}

document.addEventListener(
  'click',
  function (event) {
    if (event.target.matches('input[type="checkbox"], input[type="radio"], a[href]')) {
      return;
    }

    if (event.target.closest('custom-dropdown-sorting, .sorting')) {
      return;
    }

    let targetLabel = null;

    const clickedFacetLabel = event.target.closest(
      '#FacetFiltersForm .facets__label, #FacetFiltersFormMobile .facets__label',
    );

    if (clickedFacetLabel) {
      targetLabel = clickedFacetLabel;
    }

    const dropdownItem = event.target.closest('custom-dropdown:not(.sorting-dropdown) .dropdown-item');

    if (targetLabel) {
      event.preventDefault();
      event.stopPropagation();

      const checkbox = targetLabel.querySelector('input[type="checkbox"]');
      if (!checkbox) return;
      if (checkbox.disabled) return;

      checkbox.checked = !checkbox.checked;
      const isActive = checkbox.checked;

      if (!isActive) {
        localStorage.setItem(`reset_filter_${checkbox.name}_${checkbox.value}`, 'true');
      } else {
        localStorage.removeItem(`reset_filter_${checkbox.name}_${checkbox.value}`);
      }

      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (dropdownItem) {
      event.preventDefault();
      event.stopPropagation();

      const checkbox = dropdownItem.querySelector('input[type="checkbox"]');
      if (!checkbox) return;
      if (checkbox.disabled) return;

      const wasSelected = dropdownItem.getAttribute('aria-selected') === 'true';
      const isSelected = !wasSelected;
      dropdownItem.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      checkbox.checked = isSelected;

      if (!isSelected) {
        localStorage.setItem(`reset_filter_${checkbox.name}_${checkbox.value}`, 'true');
      } else {
        localStorage.removeItem(`reset_filter_${checkbox.name}_${checkbox.value}`);
      }

      document
        .querySelectorAll(`input[type="checkbox"][name="${checkbox.name}"][value="${checkbox.value}"]`)
        .forEach((otherCheckbox) => {
          if (otherCheckbox === checkbox) return;
          otherCheckbox.checked = isSelected;
          const otherLabel = otherCheckbox.closest('.facets__label');
          if (otherLabel) {
            if (isSelected) otherLabel.classList.add('active');
            else otherLabel.classList.remove('active');
          }
          const otherDropdownItem = otherCheckbox.closest('.dropdown-item');
          if (otherDropdownItem) {
            otherDropdownItem.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          }
        });

      const formElement = dropdownItem.closest('facet-filters-form');
      if (formElement) {
        const changeKey = `${checkbox.name}:${checkbox.value}`;
        formElement.pendingChanges.set(changeKey, isSelected);

        formElement.debouncedFilterSubmit(formElement, event);
      }
    }
  },
  true,
);

document.addEventListener('change', function (event) {
  if (event.target.type === 'checkbox') {
    if (event.target.closest('custom-dropdown-sorting, .sorting')) {
      return;
    }

    const checkbox = event.target;
    const formElement = checkbox.closest('facet-filters-form');

    syncCheckboxVisualState(checkbox);

    if (formElement) {
      const changeKey = `${checkbox.name}:${checkbox.value}`;
      formElement.pendingChanges.set(changeKey, checkbox.checked);
      FacetFiltersForm.organizeAdvancedFilters(formElement);

      formElement.debouncedFilterSubmit(formElement, event);
    }
  }
});

let initialSetupComplete = false;

document.addEventListener('DOMContentLoaded', () => {
  if (initialSetupComplete) return;
  initialSetupComplete = true;

  bindFacetFormRelocateListeners();
  relocateFacetFiltersForm();

  synchronizeVisualToCheckboxState();
  updatePriceRangesWithVAT();

  const facetsCloseButton = document.getElementById('facetsCloseButton');
  if (facetsCloseButton) {
    const productCountAttr = facetsCloseButton.getAttribute('data-product-count');
    if (productCountAttr) {
      const productCount = parseInt(productCountAttr, 10);
      if (!isNaN(productCount)) {
        FacetFiltersForm.updateFacetsCloseButton(productCount);
      }
    } else {
      const productCountElement = document.getElementById('ProductCount');
      if (productCountElement) {
        const productCount = FacetFiltersForm.extractProductCount(productCountElement.innerHTML);
        if (productCount !== null) {
          FacetFiltersForm.updateFacetsCloseButton(productCount);
        }
      }
    }
  }

  FacetFiltersForm.toggleMostSearched(document);

  document.addEventListener('change', function (event) {
    if (event.target && event.target.classList.contains('vat-toggle-input')) {
      updatePriceRangesWithVAT();
    }
  });
});

const originalRenderFilters = FacetFiltersForm.renderFilters;
FacetFiltersForm.renderFilters = function (html, event) {
  originalRenderFilters(html, event);

  requestAnimationFrame(() => {
    if (typeof window.relocateFacetFiltersForm === 'function') window.relocateFacetFiltersForm();
    window.FacetTierShowMore?.syncAll?.(document);

    synchronizeVisualToCheckboxState();

    let shouldUpdatePriceRanges = !event;

    if (event && event.target) {
      const isVatToggleEvent = event.target.classList.contains('vat-toggle-input');
      const isPriceRangeEvent = event.isPriceRangeEvent || event.target.closest('price-range');
      const isPriceFilterEvent =
        event.target.name === 'filter.v.price.gte' || event.target.name === 'filter.v.price.lte';

      shouldUpdatePriceRanges = isVatToggleEvent || isPriceRangeEvent || isPriceFilterEvent;
    }

    if (shouldUpdatePriceRanges) {
      updatePriceRangesWithVAT();
    }

    document.querySelectorAll('facet-filters-form').forEach((form) => {
      form.setupCustomDropdowns();
      FacetFiltersForm.organizeAdvancedFilters(form);
    });

    toggleVatSwitcherBorder();
  });
};

function resetFilter(name, value) {
  document.querySelectorAll(`input[type="checkbox"][name="${name}"][value="${value}"]`).forEach((checkbox) => {
    checkbox.checked = false;
    const label = checkbox.closest('.facets__label');
    if (label) {
      label.classList.remove('active');
    }
    const dropdownItem = checkbox.closest('.dropdown-item');
    if (dropdownItem) {
      dropdownItem.setAttribute('aria-selected', 'false');
    }
  });
}

function syncCheckboxVisualState(checkbox) {
  const isChecked = checkbox.checked;
  const label = checkbox.closest('.facets__label');
  if (label) {
    label.classList.toggle('active', isChecked);
  }
  const dropdownItem = checkbox.closest('.dropdown-item');
  if (dropdownItem) {
    dropdownItem.setAttribute('aria-selected', isChecked ? 'true' : 'false');
  }
  if (checkbox.classList.contains('swatch-input__input')) {
    checkbox.classList.toggle('active', isChecked);
  }
}

function synchronizeVisualToCheckboxState() {
  document
    .querySelectorAll('.facets__label input[type="checkbox"], .dropdown-item input[type="checkbox"]')
    .forEach((checkbox) => {
      syncCheckboxVisualState(checkbox);
    });
}

function updatePriceRangesWithVAT() {
  const priceRanges = document.querySelectorAll('price-range');
  const vatMultiplier = 1.21;
  const vatToggle = document.querySelector('.vat-toggle-input');
  const displayWithVat = !vatToggle || !vatToggle.checked;

  function formatNumber(num) {
    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  priceRanges.forEach((priceRange) => {
    const lowerBubble = priceRange.querySelector('.lower-bubble');
    const upperBubble = priceRange.querySelector('.upper-bubble');
    const maxValue = priceRange.querySelector('.max-value');
    const lowerRange = priceRange.querySelector('.lower-range');
    const upperRange = priceRange.querySelector('.upper-range');

    if (!lowerBubble || !upperBubble || !maxValue || !lowerRange || !upperRange) return;

    if (!priceRange.dataset.originalMaxValue) {
      priceRange.dataset.originalMaxValue = maxValue.textContent.trim();
      priceRange.dataset.rangeMax = upperRange.getAttribute('max');
    }

    const originalMaxValueText = priceRange.dataset.originalMaxValue.replace(/\s+/g, '').replace(/[^\d]/g, '');
    const rangeMax = parseInt(priceRange.dataset.rangeMax);

    if (originalMaxValueText) {
      const numericMaxValue = parseInt(originalMaxValueText);
      const displayMaxValue = displayWithVat ? numericMaxValue * vatMultiplier : numericMaxValue;
      maxValue.textContent = formatNumber(Math.round(displayMaxValue));
    }

    function updateBubbles() {
      const lowerValue = parseInt(lowerRange.value);
      let upperValue = parseInt(upperRange.value);

      const displayLowerValue = displayWithVat ? lowerValue * vatMultiplier : lowerValue;
      lowerBubble.textContent = formatNumber(Math.round(displayLowerValue));

      if (upperValue >= rangeMax - 5) {
        upperRange.value = rangeMax;
        upperValue = rangeMax;
        upperBubble.textContent = maxValue.textContent;
      } else {
        const displayUpperValue = displayWithVat ? upperValue * vatMultiplier : upperValue;
        upperBubble.textContent = formatNumber(Math.round(displayUpperValue));
      }

      updateSelectedRange();
    }

    function updateSelectedRange() {
      const rangeSelected = priceRange.querySelector('.range-selected');
      if (rangeSelected) {
        const min = parseInt(lowerRange.min);
        const max = parseInt(upperRange.max);
        const lower = parseInt(lowerRange.value);
        const upper = parseInt(upperRange.value);

        const lowerPercent = ((lower - min) / (max - min)) * 100;
        const upperPercent = ((upper - min) / (max - min)) * 100;

        rangeSelected.style.left = `${lowerPercent}%`;
        rangeSelected.style.width = `${upperPercent - lowerPercent}%`;
      }
    }

    updateBubbles();

    lowerRange.addEventListener('input', updateBubbles);
    upperRange.addEventListener('input', updateBubbles);
  });

  const activeFilters = document.querySelectorAll('.price-range-filter');
  activeFilters.forEach((filter) => {
    if (!filter.dataset.original) {
      filter.dataset.original = filter.textContent.trim();
    }

    let min = parseInt(filter.dataset.min || 0);
    let max = parseInt(filter.dataset.max || 0);

    min = Math.round(min / 100);
    max = Math.round(max / 100);

    if (min || max) {
      const displayMin = displayWithVat ? min * vatMultiplier : min;
      const displayMax = displayWithVat ? max * vatMultiplier : max;

      const formattedMin = formatMoney(Math.round(displayMin));
      const formattedMax = formatMoney(Math.round(displayMax));

      filter.textContent = `${formattedMin} - ${formattedMax}`;
    }
  });
}

function toggleVatSwitcherBorder() {
  const activeFacetsDesktop = document.querySelector('.active-facets-desktop');
  const form = document.querySelector('#FacetFiltersForm');

  if (activeFacetsDesktop && form) {
    const vatSwitcher = activeFacetsDesktop.querySelector('.facets__vat-switcher');
    if (vatSwitcher && vatSwitcher.offsetParent !== null) {
      form.style.borderTop = '0.2rem solid var(--border-color-neutral)';
      form.style.marginTop = '1rem';
      form.style.paddingTop = '1rem';
    } else {
      form.style.borderTop = 'none';
      form.style.marginTop = '0';
      form.style.paddingTop = '0';
    }
  }
}

function getCollectionHashScrollOffset() {
  let offset = 16;
  const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
  if (!Number.isNaN(headerH) && headerH > 0) offset += headerH;
  return offset;
}

function scrollToCollectionHashAnchor(hash) {
  const id = hash.replace(/^#/, '');
  if (!id) return false;
  const target = document.getElementById(id);
  if (!target) return false;

  const nextUrl = `${window.location.pathname}${window.location.search}${hash}`;
  const state = history.state ?? { searchParams: window.location.search.replace(/^\?/, '') };
  history.pushState(state, '', nextUrl);

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollToTarget = () => {
    const top = target.getBoundingClientRect().top + window.scrollY - getCollectionHashScrollOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
  };
  requestAnimationFrame(() => requestAnimationFrame(scrollToTarget));
  return true;
}

document.addEventListener('click', (e) => {
  const anchorLink = e.target.closest('a[href]');
  if (anchorLink && document.getElementById('product-grid')) {
    if (
      !anchorLink.closest('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetSortForm, facet-remove, .mobile-facets')
    ) {
      const href = (anchorLink.getAttribute('href') || '').trim();
      if (href.startsWith('#')) {
        if (scrollToCollectionHashAnchor(href)) e.preventDefault();
        return;
      }
      if (href.includes('#')) {
        try {
          const url = new URL(href, window.location.href);
          if (url.pathname === window.location.pathname && url.hash) {
            if (scrollToCollectionHashAnchor(url.hash)) e.preventDefault();
            return;
          }
        } catch (_) {
          /* ignore malformed href */
        }
      }
    }
  }

  const link = e.target.closest('a.active-filters-jump-link[href="#active-filters-content"]');
  if (!link) return;
  const target = document.getElementById('active-filters-content');
  if (!target) return;
  e.preventDefault();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });

  const desktopMq = window.matchMedia('(min-width: 990px)');
  if (!desktopMq.matches) return;

  const highlightDelayMs = reducedMotion ? 80 : 560;
  window.setTimeout(() => {
    target.classList.remove('active-filters-content--bounce');
    void target.offsetWidth;
    target.classList.add('active-filters-content--bounce');
    const clear = () => target.classList.remove('active-filters-content--bounce');
    if (reducedMotion) {
      window.setTimeout(clear, 900);
    } else {
      target.addEventListener('animationend', clear, { once: true });
      window.setTimeout(clear, 1200);
    }
  }, highlightDelayMs);
});

document.addEventListener('DOMContentLoaded', () => {
  if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.resortProductsByAvailablePrice) {
    FacetFiltersForm.resortProductsByAvailablePrice();
  }
  if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.initCollectionToolbarStickyState) {
    FacetFiltersForm.initCollectionToolbarStickyState();
  }
  toggleVatSwitcherBorder();
});
